// Design tokens da tela de configuração do canal.
// Referência: .md/DESIGN_UIUX.md (Painel de Configurações).
//
// Linguagem visual:
// - Neutros zinc (#18181b → #a1a1aa) sobre fundo #f4f4f7; roxo
//   (#6d28d9) SÓ como acento — aba ativa, ação primária, seleção, links.
// - Cor semântica comunica estado (verde = conectado, âmbar = pendente,
//   vermelho = ação destrutiva); nunca decora.
// - Largura total da área principal (full-bleed) — sem card-shell / max-width.
// - Header sticky flat (branco + border-b), sem radius.
// - Hierarquia por cabeçalho de seção (rótulo uppercase fora do cartão).

// A casca sem a animação de entrada: o esqueleto usa só isto, e quem desliza
// para dentro é o conteúdo real quando os dados chegam.
const SHELL = 'flex flex-col min-h-full w-full bg-[#f4f4f7] text-[#18181b]';

export const CANAL_DETALHE_DESIGN = {
    // ---------- shell ----------
    root: `${SHELL} animate-in fade-in slide-in-from-bottom-4 duration-500`,
    // Aplicada inline: Tailwind não é a melhor ferramenta para um stack de
    // família com fallbacks, e a fonte é escopada só a esta tela.
    fontStack: "'Plus Jakarta Sans', Manrope, ui-sans-serif, system-ui, sans-serif",
    container: 'w-full px-4 md:px-6 lg:px-8',
    pageBody: 'pb-[120px]',

    // ---------- barra de topo + abas (bloco fixo único) ----------
    // Topo e abas no mesmo sticky. Barra flat full-width — sem borda completa
    // nem radius (cantos arredondados deixam o scroll aparecer por trás).
    stickyHeader: 'sticky top-0 z-30 bg-white border-b border-[#e7e7ec]',
    topbar: 'flex flex-col md:flex-row md:items-start gap-4 pt-[18px] pb-0',
    topbarIdentity: 'flex items-start gap-4 min-w-0 flex-1',
    backButton:
        'flex items-center justify-center flex-shrink-0 w-[34px] h-[34px] mt-[3px] rounded-lg bg-transparent text-[#71717a] transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b]',
    // Ladrilho do ícone da plataforma. A cor de fundo vem por style inline
    // (PLATAFORMA_UI) — cada canal traz a sua, e não são classes estáticas.
    platformTile: 'flex items-center justify-center flex-shrink-0 w-[42px] h-[42px] rounded-[10px] overflow-hidden',
    titleRow: 'flex items-baseline gap-2.5 flex-wrap min-w-0',
    title: 'text-[20px] font-bold tracking-[-0.02em] leading-tight text-[#18181b] truncate',
    handle: 'text-[13px] font-medium text-[#a1a1aa] truncate',
    statusRow: 'flex items-center gap-[7px] mt-[5px] flex-wrap',
    statusDot: 'w-1.5 h-1.5 rounded-full flex-shrink-0',
    statusDotOk: 'bg-[#17b26a]',
    statusDotWarn: 'bg-[#f79009]',
    statusDotNeutral: 'bg-[#d4d4d8]',
    statusText: 'text-[12.5px] font-semibold',
    statusTextOk: 'text-[#067647]',
    statusTextWarn: 'text-[#b54708]',
    statusTextNeutral: 'text-[#71717a]',
    statusSep: 'text-[#e0e0e5]',
    statusMeta: 'text-[12.5px] text-[#71717a]',

    topbarActions: 'flex items-center gap-3.5 flex-shrink-0 md:pt-[5px]',
    dirtyNote: 'text-[13px] text-[#b54708] whitespace-nowrap',
    savedNote: 'text-[13px] text-[#a1a1aa] whitespace-nowrap',

    // ---------- abas de nível 1 ----------
    // p-0/py-0 antes dos paddings finais: neutraliza o padding do TabsList/
    // TabsTrigger base para que o merge do Tailwind não dependa da ordem gerada.
    // overflow-y-hidden é obrigatório: com overflow-x auto o eixo Y vira auto
    // sozinho e o box-shadow da aba ativa basta para o navegador desenhar uma barra.
    tabsList:
        'flex h-auto w-full items-center justify-start gap-[26px] rounded-none bg-transparent p-0 mt-3.5 overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden backdrop-blur-none',
    tab:
        'flex-shrink-0 rounded-none bg-transparent px-0.5 py-0 pt-2.5 pb-3 text-[14px] font-medium text-[#71717a] whitespace-nowrap transition-colors hover:text-[#18181b] data-[state=active]:bg-transparent data-[state=active]:text-[#6d28d9] data-[state=active]:font-semibold data-[state=active]:shadow-[inset_0_-2px_0_#6d28d9]',
    tabsBody: 'pt-6',

    // ---------- cabeçalho de seção (fora do cartão) ----------
    sectionHeader: 'mt-[18px] first:mt-0',
    sectionHeaderTitle: 'text-[13px] font-bold uppercase tracking-[0.06em] text-[#a1a1aa]',
    sectionHeaderSub: 'mt-1.5 text-[13px] text-[#71717a]',

    // ---------- cartões ----------
    stack: 'flex flex-col gap-3.5',
    card: 'bg-white border border-[#e7e7ec] rounded-[12px] p-[26px] overflow-hidden min-w-0',
    // Variante sem padding: para cartões que têm faixas de largura total
    // (cabeçalho + rodapé com fundo próprio), como o bloco de conexão.
    cardFlush: 'bg-white border border-[#e7e7ec] rounded-[12px] overflow-hidden min-w-0',
    // Variante para listas de linhas com toggle: o padding vertical é de cada linha.
    cardRows: 'bg-white border border-[#e7e7ec] rounded-[12px] px-[26px] py-2 overflow-hidden min-w-0',
    cardTitle: 'text-[15px] font-semibold text-[#18181b]',
    cardSubtitle: 'mt-0.5 text-[13px] text-[#71717a] leading-[1.5]',
    cardHead: 'flex items-start justify-between gap-4',
    section: 'mt-5 pt-5 border-t border-[#f0f0f3]',
    identityLinkBlock: 'mt-6 pt-6 border-t border-[#f0f0f3]',
    sectionTitle: 'text-[14px] font-semibold text-[#18181b]',
    sectionSubtitle: 'mt-0.5 text-[13px] text-[#71717a] leading-[1.5]',
    divider: 'h-px bg-[#f0f0f3]',

    // ---------- formulário ----------
    grid2: 'grid grid-cols-1 md:grid-cols-2 gap-6',
    field: 'min-w-0',
    label: 'block text-[13px] font-semibold text-[#18181b] mb-[7px]',
    required: 'text-[#6d28d9]',
    optional: 'text-[12px] font-medium text-[#a1a1aa]',
    hint: 'mt-[7px] text-[12px] text-[#a1a1aa] leading-[1.5]',
    hintWarn: 'mt-[7px] text-[12px] font-medium text-[#b54708] leading-[1.5]',
    hintError: 'mt-[7px] text-[12px] font-medium text-[#b42318] leading-[1.5]',
    // md:text-[14px] repete o tamanho de propósito: o Input base traz md:text-sm,
    // e sem o par no mesmo breakpoint ele venceria a partir de 768px.
    input:
        'w-full box-border h-auto px-3.5 py-[11px] rounded-lg border border-[#d4d4d8] bg-white text-[14px] md:text-[14px] text-[#18181b] shadow-none transition-colors placeholder:text-[#a1a1aa] hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus-visible:border-[#8b5cf6] focus-visible:ring-2 focus-visible:ring-[#c4b5fd] focus-visible:ring-offset-0',
    inputMono: 'font-mono text-[13px]',
    inputShort: 'w-[120px]',
    // Wrapper para input + sufixo textual ("minutos").
    inputSuffixRow: 'flex items-center gap-2.5',
    inputSuffix: 'text-[13px] text-[#71717a]',
    select:
        'w-full box-border h-auto min-h-[44px] px-3.5 py-[11px] rounded-lg border border-[#d4d4d8] bg-white text-[14px] md:text-[14px] font-normal text-[#18181b] shadow-none transition-colors hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus:ring-2 focus:ring-[#c4b5fd] disabled:bg-[#fafafb] disabled:text-[#a1a1aa]',
    multiSelect:
        'w-full box-border min-h-[44px] px-2.5 py-[7px] rounded-lg border border-[#d4d4d8] bg-white hover:bg-white text-[14px] shadow-none',
    textarea:
        'w-full box-border px-4 py-3.5 rounded-lg border border-[#d4d4d8] bg-white text-[14px] leading-[1.6] text-[#18181b] shadow-none resize-y transition-colors placeholder:text-[#a1a1aa] hover:border-[#b8b8c0] focus:border-[#8b5cf6] focus-visible:border-[#8b5cf6] focus-visible:ring-2 focus-visible:ring-[#c4b5fd] focus-visible:ring-offset-0',
    // Campo somente leitura que mostra um valor definido em outro lugar
    // (a plataforma vem da conexão, não é escolhida aqui).
    readonlyField: 'flex items-center gap-2.5 px-3.5 py-[9px] rounded-lg border border-[#d4d4d8] bg-[#fafafb] min-h-[44px]',
    readonlyValue: 'text-[14px] font-medium text-[#18181b]',
    readonlyNote: 'ml-auto text-[12px] text-[#a1a1aa] whitespace-nowrap',

    // ---------- variáveis + prévia da mensagem ----------
    editorGrid: 'grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-7 items-start',
    editorGridNarrow: 'grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6 items-start',
    varsRow: 'flex flex-wrap items-center gap-2 mt-2.5',
    varChip:
        'px-[9px] py-1 rounded-md bg-[#f4f4f5] text-[12px] font-medium text-[#52525b] transition-colors cursor-pointer hover:bg-[#ede9fe] hover:text-[#5b21b6]',
    varsHint: 'ml-auto text-[12px] text-[#a1a1aa]',
    previewLabel: 'text-[12px] font-semibold text-[#a1a1aa] mb-2',
    previewShell: 'bg-[#e8eef4] rounded-[10px] p-4',
    previewBubble:
        'bg-white rounded-lg rounded-tl-[3px] px-3.5 py-3 text-[13px] leading-[1.6] text-[#18181b] shadow-[0_1px_2px_rgba(24,24,27,.08)] whitespace-pre-wrap break-words',
    previewEmpty: 'text-[#a1a1aa] italic',
    previewFoot: 'mt-2 text-[11px] text-[#8a9aab] text-right',

    // ---------- linha com toggle ----------
    toggleRow: 'flex items-center gap-4 py-[18px]',
    toggleRowDivided: 'border-b border-[#f0f0f3]',
    toggleRowMain: 'flex-1 min-w-0',
    toggleRowLabelWrap: 'flex items-center gap-2 flex-wrap',
    toggleRowLabel: 'text-[14px] font-semibold text-[#18181b]',
    toggleRowHelp: 'mt-[3px] text-[13px] text-[#71717a] leading-[1.5] max-w-[620px]',
    betaBadge: 'px-[7px] py-0.5 rounded-md bg-[#ede9fe] text-[11px] font-bold text-[#5b21b6]',
    // O Switch base é 36×20; aqui só trocamos a cor para o roxo do sistema.
    switch: 'flex-shrink-0 data-[state=checked]:bg-[#6d28d9] data-[state=unchecked]:bg-[#d4d4d8]',

    // ---------- cartões de escolha (radio em forma de cartão) ----------
    choiceGrid: 'grid grid-cols-1 md:grid-cols-3 gap-2.5',
    choiceCard: 'text-left px-3.5 py-[13px] rounded-lg border transition-colors cursor-pointer',
    choiceCardOn: 'bg-[#ede9fe] border-[#a78bfa]',
    choiceCardOff: 'bg-white border-[#e7e7ec] hover:border-[#d4d4d8]',
    choiceTitle: 'text-[13.5px] font-semibold',
    choiceTitleOn: 'text-[#5b21b6]',
    choiceTitleOff: 'text-[#18181b]',
    choiceHelp: 'mt-[3px] text-[12px] text-[#71717a] leading-[1.45]',

    // ---------- trilho lateral (lista de mensagens) ----------
    railWrap: 'bg-white border border-[#e7e7ec] rounded-[12px] overflow-hidden grid grid-cols-1 md:grid-cols-[236px_1fr]',
    railCol: 'md:border-r border-b md:border-b-0 border-[#f0f0f3] bg-[#fafafb] p-3 flex md:flex-col gap-0.5 overflow-x-auto md:overflow-visible h-auto rounded-none justify-start',
    // md:w-full e não w-full: em telas pequenas o trilho vira uma faixa horizontal
    // rolável, e largura total ali empilharia um item por tela.
    railItem:
        'flex-shrink-0 justify-start gap-2 md:w-full px-[13px] py-[11px] rounded-lg text-[13.5px] font-medium text-[#3f3f46] whitespace-nowrap border border-transparent bg-transparent shadow-none transition-colors hover:bg-[#f4f4f5] data-[state=active]:bg-[#ede9fe] data-[state=active]:border-[#ddd6fe] data-[state=active]:text-[#5b21b6] data-[state=active]:font-semibold data-[state=active]:shadow-none',
    railDot: 'w-[7px] h-[7px] rounded-full flex-shrink-0',
    railDotOn: 'bg-[#17b26a]',
    railDotOff: 'bg-[#d4d4d8]',
    railPane: 'p-[26px] min-w-0',
    railPaneHead: 'flex items-start gap-3.5 mb-5',

    // ---------- botões ----------
    primaryButton:
        'inline-flex items-center justify-center gap-2.5 px-5 py-2.5 rounded-[10px] bg-gradient-to-b from-[#8b5cf6] to-[#7c3aed] text-white text-[14px] font-semibold whitespace-nowrap shadow-[0_2px_8px_rgba(124,58,237,.32)] transition-colors hover:from-[#7c3aed] hover:to-[#6d28d9] disabled:from-[#e4e4e7] disabled:to-[#e4e4e7] disabled:text-[#a1a1aa] disabled:shadow-none disabled:cursor-not-allowed',
    ghostButton:
        'inline-flex items-center justify-center gap-2 px-4 py-[9px] rounded-lg border border-[#e7e7ec] bg-white text-[13px] font-semibold text-[#52525b] whitespace-nowrap transition-colors hover:bg-[#f4f4f7] hover:text-[#18181b] disabled:opacity-50 disabled:cursor-not-allowed',
    dangerButton:
        'inline-flex items-center justify-center gap-2 px-4 py-[9px] rounded-lg border border-[#e7e7ec] bg-white text-[13px] font-semibold text-[#b42318] whitespace-nowrap transition-colors hover:bg-[#fef3f2] hover:border-[#fecdca] disabled:opacity-50 disabled:cursor-not-allowed',
    warnButton:
        'inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg border border-[#f7d9a8] bg-white text-[12.5px] font-semibold text-[#b54708] whitespace-nowrap transition-colors hover:bg-[#fffaf0]',
    link: 'text-[13px] font-semibold text-[#6d28d9] transition-colors hover:text-[#5b21b6] hover:underline',
    linkMuted: 'text-[12.5px] font-medium text-[#a1a1aa] transition-colors hover:text-[#52525b] hover:underline',

    // ---------- estado / identidade ----------
    // Cabeçalho do cartão de conexão: avatar + nome + ação, largura total.
    connectionHead: 'flex flex-col sm:flex-row sm:items-center gap-4 px-[26px] py-[22px]',
    identityRow: 'flex items-center gap-4 flex-1 min-w-0',
    identityIcon: 'flex items-center justify-center flex-shrink-0 w-11 h-11 rounded-[10px] overflow-hidden',
    identityName: 'text-[15px] font-semibold text-[#18181b] truncate',
    identityMeta: 'mt-0.5 text-[13px] text-[#71717a] truncate',

    badge: 'inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-md text-[12px] font-semibold whitespace-nowrap flex-shrink-0 border',
    badgeOk: 'bg-[#ecfdf3] text-[#067647] border-[#abefc6]',
    badgeWarn: 'bg-[#fffaeb] text-[#b54708] border-[#fedf89]',
    badgeNeutral: 'bg-[#f4f4f5] text-[#71717a] border-[#e7e7ec]',
    badgeDot: 'w-1.5 h-1.5 rounded-full',
    badgeDotOk: 'bg-[#17b26a]',
    badgeDotWarn: 'bg-[#f79009]',
    badgeDotNeutral: 'bg-[#a1a1aa]',

    // ---------- bloco expansível (Telegram Business) ----------
    disclosure: 'border-t border-[#f0f0f3] bg-[#fafafb]',
    disclosureButton: 'w-full flex items-center gap-3 px-[26px] py-4 text-left cursor-pointer transition-colors hover:bg-[#f4f4f7] min-w-0',
    disclosureCaret: 'text-[11px] text-[#a1a1aa] w-2.5 flex-shrink-0 transition-transform',
    disclosureCaretOpen: 'rotate-90',
    disclosureBody: 'px-[26px] pb-[22px] pl-12 flex flex-col gap-3',

    // ---------- passos numerados ----------
    stepRow: 'flex items-start gap-3',
    stepNumber:
        'flex items-center justify-center flex-shrink-0 w-5 h-5 rounded-full bg-[#ede9fe] text-[11px] font-bold text-[#6d28d9]',
    stepText: 'text-[13px] text-[#3f3f46] leading-[1.55]',
    stepFoot: 'pl-8 text-[12px] text-[#a1a1aa]',

    // ---------- esqueleto da carga inicial ----------
    // As peças repetem as medidas do conteúdo real (ladrilho 42px, voltar 34px,
    // campo 44px) e são montadas dentro dos mesmos shell/container/topbar — assim
    // nada muda de lugar quando os dados chegam.
    // animate-pulse fica no contêiner, não em cada peça: um pulso só para o
    // bloco inteiro, igual à lista de canais.
    skeleton: {
        root: SHELL,
        pulse: 'animate-pulse',
        back: 'w-[34px] h-[34px] mt-[3px] rounded-lg bg-[#f4f4f5] flex-shrink-0',
        tile: 'w-[42px] h-[42px] rounded-[10px] bg-[#f4f4f5] flex-shrink-0',
        line: 'bg-[#f4f4f5] rounded',
        button: 'h-[38px] rounded-lg bg-[#f4f4f5]',
        input: 'h-[44px] rounded-lg bg-[#f4f4f5]',
    },

    // ---------- blocos auxiliares ----------
    noticeWarn: 'px-4 py-3.5 rounded-[10px] border border-[#fedf89] bg-[#fffaeb] text-[13px] text-[#b54708] leading-[1.5]',
    noticeInfo: 'px-4 py-3.5 rounded-[10px] border border-[#e7e7ec] bg-[#fafafb] text-[13px] text-[#71717a] leading-[1.5]',
    footnote: 'mt-3 text-[12px] text-[#a1a1aa] leading-[1.5]',
    emptyState: 'px-2 py-6 text-center text-[13px] text-[#a1a1aa]',
};

export default CANAL_DETALHE_DESIGN;
